"use client";

import { useEffect, useRef } from "react";
import useStore from "@/store/useStore";
import {
  getCachedUserMetadata,
  initializeHotjarWithUserData,
  clearUserMetadataCache,
} from "@/utils/userMetadata";
import { isAdmin, isSuperAdmin } from "@/utils/authHelpers";
import { Analytics } from "@vercel/analytics/next";

export default function AnalyticsProvider({ children }) {
  const session = useStore((state) => state.session);
  const isInitialized = useStore((state) => state.isInitialized);
  const initializedRef = useRef(false);
  const lastUserIdRef = useRef(null);
  const scriptsLoadedRef = useRef(false);

  // Load analytics scripts conditionally based on user role
  useEffect(() => {
    // Only run in production
    if (process.env.NODE_ENV === "development") {
      return;
    }

    // Skip analytics on staging environment
    if (
      typeof window !== "undefined" &&
      window.location.hostname === "staging.qensa.me"
    ) {
      console.log(
        "🚫 Skipping analytics script loading on staging environment"
      );
      return;
    }

    const loadAnalyticsScripts = async () => {
      // Skip if already loaded or if user is admin
      if (scriptsLoadedRef.current) {
        return;
      }

      // Check if user is admin or super admin - skip analytics for these users
      if (session?.user) {
        const userIsAdmin = isAdmin(session);
        const userIsSuperAdmin = isSuperAdmin(session);

        if (userIsAdmin || userIsSuperAdmin) {
          console.log("🚫 Skipping analytics script loading for admin user:", {
            userId: session.user.id?.slice(0, 8) + "...",
            isAdmin: userIsAdmin,
            isSuperAdmin: userIsSuperAdmin,
          });
          return;
        }
      }

      // Load Umami script
      const umamiScript = document.createElement("script");
      umamiScript.defer = true;
      umamiScript.src = "https://cloud.umami.is/script.js";
      umamiScript.setAttribute(
        "data-website-id",
        "e26b4141-6df7-4002-8f74-d5b1c862f2a9"
      );

      // Add user identification to Umami if user is authenticated
      if (session?.user) {
        try {
          const userMetadata = await getCachedUserMetadata(session);
          if (userMetadata.isAuthenticated) {
            // Set user data attributes for Umami
            umamiScript.setAttribute("data-user-id", userMetadata.userId);
            umamiScript.setAttribute("data-user-email", userMetadata.email);
            umamiScript.setAttribute(
              "data-user-role",
              userMetadata.userRole || "user"
            );
            umamiScript.setAttribute(
              "data-subscription-tier",
              userMetadata.subscriptionTier || "free"
            );

            console.log("📊 Loading Umami with user identification:", {
              userId: userMetadata.userId,
              email: userMetadata.email,
              userRole: userMetadata.userRole,
            });
          }
        } catch (error) {
          console.warn("Error getting user metadata for Umami:", error);
        }
      }

      document.head.appendChild(umamiScript);

      // Load Hotjar script
      const hotjarScript = document.createElement("script");
      hotjarScript.innerHTML = `
        (function(h,o,t,j,a,r){
          h.hj=h.hj||function(){(h.hj.q=h.hj.q||[]).push(arguments)};
          h._hjSettings={hjid:5232555,hjsv:6};
          a=o.getElementsByTagName('head')[0];
          r=o.createElement('script');r.async=1;
          r.src=t+h._hjSettings.hjid+j+h._hjSettings.hjsv;
          a.appendChild(r);
        })(window,document,'https://static.hotjar.com/c/hotjar-','.js?sv=');
      `;
      document.head.appendChild(hotjarScript);

      // Load Google Tag Manager
      const gtmScript = document.createElement("script");
      gtmScript.src = "https://www.googletagmanager.com/gtm.js?id=GTM-WNSH3B2S";
      document.head.appendChild(gtmScript);

      const gtmDataLayer = document.createElement("script");
      gtmDataLayer.innerHTML = `
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({
          'gtm.start': new Date().getTime(),
          'event': 'gtm.js'
        });
      `;
      document.head.appendChild(gtmDataLayer);

      // Add GTM noscript fallback
      const gtmNoscript = document.createElement("noscript");
      gtmNoscript.innerHTML = `
        <iframe
          src="https://www.googletagmanager.com/ns.html?id=GTM-WNSH3B2S"
          height="0"
          width="0"
          style="display:none;visibility:hidden">
        </iframe>
      `;
      document.body.insertBefore(gtmNoscript, document.body.firstChild);

      scriptsLoadedRef.current = true;
      console.log("🔧 Analytics scripts loaded dynamically");
    };

    // Only load scripts when we have session information
    if (isInitialized && session !== undefined) {
      loadAnalyticsScripts();
    }
  }, [session, isInitialized]);

  useEffect(() => {
    // Only run in production and when auth is initialized
    if (process.env.NODE_ENV === "development" || !isInitialized) {
      return;
    }

    // Skip analytics on staging environment
    if (
      typeof window !== "undefined" &&
      window.location.hostname === "staging.qensa.me"
    ) {
      console.log(
        "🚫 Skipping analytics initialization on staging environment"
      );
      return;
    }

    const initializeAnalytics = async () => {
      try {
        const currentUserId = session?.user?.id;

        // Check if user is admin or super admin - skip analytics for these users
        if (session?.user) {
          const userIsAdmin = isAdmin(session);
          const userIsSuperAdmin = isSuperAdmin(session);

          if (userIsAdmin || userIsSuperAdmin) {
            console.log(
              "🚫 Skipping analytics initialization for admin/super admin user:",
              {
                userId: currentUserId?.slice(0, 8) + "...",
                isAdmin: userIsAdmin,
                isSuperAdmin: userIsSuperAdmin,
              }
            );
            return;
          }
        }

        // Check if we need to reinitialize (user changed or first time)
        const shouldInitialize =
          !initializedRef.current || lastUserIdRef.current !== currentUserId;

        if (!shouldInitialize) {
          return;
        }

        // Clear cache if user changed
        if (lastUserIdRef.current && lastUserIdRef.current !== currentUserId) {
          clearUserMetadataCache();
        }

        // Get user metadata
        const userMetadata = await getCachedUserMetadata(session);

        // Initialize Hotjar with user data
        initializeHotjarWithUserData(userMetadata);

        // Initialize Umami user identification
        if (
          typeof window !== "undefined" &&
          window.umami &&
          userMetadata.isAuthenticated
        ) {
          try {
            // Set user properties in Umami
            window.umami.identify({
              userId: userMetadata.userId,
              email: userMetadata.email,
              userRole: userMetadata.userRole,
              subscriptionTier: userMetadata.subscriptionTier,
              spaceCount: userMetadata.spaceCount,
            });

            console.log("📊 Umami user identified:", {
              userId: userMetadata.userId,
              email: userMetadata.email,
              userRole: userMetadata.userRole,
            });
          } catch (error) {
            console.warn("Error identifying user in Umami:", error);
          }
        }

        // Mark as initialized and update last user ID
        initializedRef.current = true;
        lastUserIdRef.current = currentUserId;

        console.log("🔧 Analytics initialized with user metadata:", {
          userId: userMetadata.userId,
          isAuthenticated: userMetadata.isAuthenticated,
          userRole: userMetadata.userRole,
          spaceCount: userMetadata.spaceCount,
        });
      } catch (error) {
        console.error("Error initializing analytics:", error);
      }
    };

    initializeAnalytics();
  }, [session, isInitialized]);

  // Clear initialization when user signs out
  useEffect(() => {
    if (!session?.user?.id && initializedRef.current) {
      console.log("🧹 Clearing analytics initialization (user signed out)");
      initializedRef.current = false;
      lastUserIdRef.current = null;
      clearUserMetadataCache();
    }
  }, [session]);

  return (
    <>
      {children}
      <Analytics />
    </>
  );
}
